import qs from 'qs'
import axios, {
  AxiosError,
  AxiosPromise,
  AxiosRequestConfig,
} from 'axios'
import { Get } from './Get'
import { Put } from './Put'
import { Post } from './Post'
// import {env} from '../Application';
import { UserPreferencesData, RegisterUserData, SsoSessionResponse } from './UserService.d'
import { store } from '~/Store'
import { UserSelectors } from '~/Store/User'
import { Delete } from './Delete'
import { SYSTEM_CONSTANT } from '~/constants/system-constant'
import { createPkceTransaction, savePkceTransaction } from '~/Utils/pkce'
import { ApiHelpers, APIHosts } from './ApiHelpers'
import i18n from '~/translations/i18n'

interface LoginResponse {
  access_token: string
  id_token?: string
  expires_in: number
  token_type: string
  refresh_token?: string
  scope: string
}

export interface ListOrganizationResponse {
  guid: string
  companyName: string
  billingAddress: string
  billingAddress2: string
  billingCity: string
  billingState: string
  billingZip: string
  crmId: string | null
  duns: string
  naics: string | null
  userOrganization: string | null
  licenses: string | null
  organizationRoles: string | null
  code?: string | null
  taxCode?: string | null
  note?: string | null
  companyId?: number | null
}

export interface User {
  id: string
  invitationStatus: string
  userName: string
  email: string
  phoneNumber: string | null
  firstname: string
  lastname: string
  avatarUrl: string | null
  forcePasswordChange: boolean
  profile: string | null
}

export interface LicenseType {
  guid: string
  offlineRange: number
  type: string
  name: string
}

export interface License {
  users: User[]
  number: number
  guid: string
  state: string | null
  licenseType: LicenseType
  organizationId: string
  userGuid: string | null
  expiredate: string
  licenseTypeId: string
  activeDate: string
  limitDevices: number
}

export interface dateLicense {
  expiredate: string
  licenseTypeId: string
  activeDate: string
  limitDevices: number
  number: number
}

export interface User {
  userOrgId: string
  organizationRoles: string[]
  id: string
  invitationStatus: string
  userName: string
  email: string
  phoneNumber: string | null
  firstname: string
  lastname: string
  avatarUrl: string | null
  forcePasswordChange: boolean
  profile: string | null
}

export interface UserListResponse {
  users: User[]
}

export interface NewLicenseData {
  expiredate: string
  licenseTypeId: string
  activeDate: string
  limitDevices: number
  number: number
}
class UserServiceController extends ApiHelpers {
  ApiHostLicense = APIHosts.LicenseManager;
  ApiHostIdentity = APIHosts.Identity;

  /** Authorization Code + PKCE (S256) — SPA không thu thập/gửi password. */
  public Auth = {
    /** Sinh PKCE, lưu tạm sessionStorage, redirect toàn trang sang `/connect/authorize`. */
    beginLogin: async (returnTo?: string): Promise<void> => {
      const { codeVerifier, codeChallenge, state, nonce } = await createPkceTransaction()
      savePkceTransaction({ codeVerifier, state, nonce }, returnTo)

      const params = new URLSearchParams({
        response_type: 'code',
        client_id: SYSTEM_CONSTANT.CLIENT_ID,
        redirect_uri: SYSTEM_CONSTANT.REDIRECT_URI,
        scope: SYSTEM_CONSTANT.SCOPE,
        code_challenge: codeChallenge,
        code_challenge_method: 'S256',
        state,
        nonce,
        // AuthServer's Login page đọc culture qua QueryStringRequestCultureProvider (Startup.cs).
        culture: i18n.language,
      })
      window.location.assign(
        `${this.getHost(this.ApiHostIdentity)}/connect/authorize?${params.toString()}`,
      )
    },

    /** Đổi authorization code lấy token — gọi `axios` thuần, không qua `authIntercept`. */
    exchangeCodeForToken: (
      code: string,
      codeVerifier: string,
    ): AxiosPromise<LoginResponse> => {
      const data = qs.stringify({
        grant_type: 'authorization_code',
        client_id: SYSTEM_CONSTANT.CLIENT_ID,
        code,
        redirect_uri: SYSTEM_CONSTANT.REDIRECT_URI,
        code_verifier: codeVerifier,
      })
      return axios.post(`${this.getHost(this.ApiHostIdentity)}/connect/token`, data, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      })
    },

    /** Đổi refresh_token lấy access_token mới, chuẩn OIDC — không gửi username/password. */
    refreshAccessToken: (
      refreshToken: string,
      orgId?: string,
    ): AxiosPromise<LoginResponse> => {
      const data = qs.stringify({
        grant_type: 'refresh_token',
        client_id: SYSTEM_CONSTANT.CLIENT_ID,
        refresh_token: refreshToken,
        ...(orgId ? { orgId } : {}),
      })
      return axios.post(`${this.getHost(this.ApiHostIdentity)}/connect/token`, data, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      })
    },

    /** Central SSO cookie check for /login. Plain axios (no Bearer token, bypasses authIntercept) with
     * `withCredentials` so the cookie rides along on this cross-origin call. */
    getSession: (): AxiosPromise<SsoSessionResponse> =>
      axios.get(`${this.getHost(this.ApiHostIdentity)}/api/auth/session`, {
        withCredentials: true,
        timeout: 10000,
      }),

    /** RP-initiated logout — redirect toàn trang, xoá cookie AuthServer. Dùng cho "Logout All". */
    logoutRedirect: (): void => {
      const params = new URLSearchParams({
        post_logout_redirect_uri: SYSTEM_CONSTANT.POST_LOGOUT_REDIRECT_URI,
      })
      window.location.assign(`${this.getHost(this.ApiHostIdentity)}/connect/logout?${params.toString()}`)
    },

    /** Logout Current App — revoke token của riêng client này qua axios, không đụng SSO cookie. */
    logoutCurrentApp: () => Post(`${this.getHost(this.ApiHostIdentity)}/api/auth/logout-current`),
  }

  public Put = {
    changePassword: (data: { oldPassword: string; newPassword: string }) =>
      Put(`${this.getHost(this.ApiHostLicense)}/users/current/password`, data),

    confirmUserEmail: (cb: string) => {
      const url = decodeURI(cb)
      return Put(url)
    },
    activateAccount: (
      cb: string,
      password: string,
      firstName?: string,
      lastName?: string,
    ) => {
      let url = decodeURI(cb)
      if (url.indexOf(location.protocol) == -1) {
        if (
          url.indexOf('https') == -1 &&
          location.protocol.indexOf('https') != -1
        ) {
          url = url.replace('http', 'https')
        }
      }
      const formData = new FormData()
      formData.append('password', password)
      if (firstName) formData.append('firstName', firstName)
      if (lastName) formData.append('lastName', lastName)
      return axios.put(url, formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      })
    },
    forgotPassword: (cb: string, password: string) => {
      let url = decodeURI(cb)
      if (url.indexOf(location.protocol) == -1) {
        if (
          url.indexOf('https') == -1 &&
          location.protocol.indexOf('https') != -1
        ) {
          url = url.replace('http', 'https')
        }
      }
      const formData = new FormData()
      formData.append('password', password)
      return axios.put(url, formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      })
    },
    confirmInvitation: (orgId: string, accept: boolean) =>
      Put(
        `${this.getHost(this.ApiHostLicense)}/users/current/organizations/${orgId}/invitationStatus?accept=${accept}`,
      ),
    updateUserProfile: (data: any) =>
      Put(`${this.getHost(this.ApiHostLicense)}/users/current`, data),
    updateDateLicense: (
      orgId: number,
      input: dateLicense,
      options: AxiosRequestConfig = {},
    ) =>
      Put(
        `${this.getHost(this.ApiHostLicense)}/organizations/${orgId}/licenses`,
        input,
        options,
      ),
  }

  public Get = {
    fetchCurrentUserAvatar: (options: AxiosRequestConfig = {}) =>
      Get(`${this.getHost(this.ApiHostLicense)}/users/current/avatar`, options),
    fetchCurrentUser: (options: AxiosRequestConfig = {}) => {
      // const state = store.getState()
      // const userEmail = UserSelectors.getEmail(state)
      // return Get(`${this.getHost(this.ApiHostLicense)}/users/${userEmail}`, options)
      return Get(`${this.getHost(this.ApiHostLicense)}/users/current/profile`, options)
    },
    fetchCurrentUserOrganizations: (options: AxiosRequestConfig = {}) =>
      Get(`${this.getHost(this.ApiHostLicense)}/users/current/organizations`, options),
    fetchCurrentUserLicenses: (options: AxiosRequestConfig = {}) =>
      Get(`${this.getHost(this.ApiHostLicense)}/users/current/licenses`, options),
    fetchCurrentUserPreferences: (options: AxiosRequestConfig = {}) =>
      Get(
        `${this.getHost(this.ApiHostLicense)}/users/current/preferences/currentClient`,
        options,
      ),
    fetchUserByEmail: (email: string, options: AxiosRequestConfig = {}) =>
      Get(`${this.getHost(this.ApiHostLicense)}/users/${email}`, options),
    fetchUserLicenses: (userId: string, options: AxiosRequestConfig = {}) =>
      Get(`${this.getHost(this.ApiHostLicense)}/users/${userId}/licenses`, options),
    fetchTokenForPasswordReset: (options: AxiosRequestConfig = {}) =>
      Get(`${this.getHost(this.ApiHostLicense)}/users/current/password/token`, options),
    getCaptchaByEmail: (email: string, options: AxiosRequestConfig = {}) => {
      return Get(
        `${this.getHost(this.ApiHostIdentity)}/clients/captcha/${encodeURIComponent(email)}`,
        options,
      )
    },
    getListOrganization: (
      options: AxiosRequestConfig = {},
    ): AxiosPromise<ListOrganizationResponse[] | AxiosError> =>
      Get(`${this.getHost(this.ApiHostLicense)}/organizations`, options),

    getLicensesById: (
      orgId: number,
      options: AxiosRequestConfig = {},
    ): AxiosPromise<ListOrganizationResponse[] | AxiosError> =>
      Get(`${this.getHost(this.ApiHostLicense)}/organizations/${orgId}/licenses`, options),

    getUsersByOrganization: (
      orgId: string,
      options: AxiosRequestConfig = {},
    ): AxiosPromise<UserListResponse | AxiosError> =>
      Get(`${this.getHost(this.ApiHostLicense)}/organizations/${orgId}/users`, options),
  }

  public Post = {
    registerUser: (data: RegisterUserData, options: AxiosRequestConfig = {}) =>
      Post(`${this.getHost(this.ApiHostLicense)}/users`, data, options),
    resetPasswordEmail: (email: string, options: AxiosRequestConfig = {}) =>
      Post(
        `${this.getHost(this.ApiHostLicense)}/users/${email}/notify/passwordreset`,
        {},
        options,
      ),
    currentUserPreferences: (
      data: UserPreferencesData,
      options: AxiosRequestConfig = {},
    ) =>
      Post(
        `${this.getHost(this.ApiHostLicense)}/users/current/preferences`,
        data,
        options,
      ),
    createUser: (data: RegisterUserData) =>
      Post(`${this.getHost(this.ApiHostLicense)}/users/create`, data),

    createNewUser: (
      userId: number,
      input: NewLicenseData[],
      options: AxiosRequestConfig = {},
    ) => {
      Post(
        `${this.getHost(this.ApiHostLicense)}/organizations/users/${userId}/licenses`,
        input,
        options,
      )
    },
    createNewLicense: (
      orgId: string,
      input: NewLicenseData,
      options: AxiosRequestConfig = {},
    ) => {
      Post(
        `${this.getHost(this.ApiHostLicense)}/organizations/${orgId}/licenses`,
        input,
        options,
      )
    },
  }

  public Delete = {
    deleteUser: (
      userId: number,
      input: string[],
      options: AxiosRequestConfig = {},
    ) => {
      Delete(
        `${this.getHost(this.ApiHostLicense)}/organizations/users/${userId}/licenses`,
        input,
        options,
      )
    },
    deleteClaim: (productClaimId: string, options: AxiosRequestConfig = {}) => {
      Delete(`${this.getHost(this.ApiHostLicense)}/Claims/${productClaimId}`, options)
    },
  }
}

export const UserService = new UserServiceController()
